// aiTokenLog.js — token usage tracking for Hayah AI calls.
//
// Captures Gemini's response.usageMetadata and accumulates it into
// localStorage["hayahTokenUsage"]:
//
//   {
//     sinceTs: number,            // ms — when counter started / last reset
//     totals:    { in, out, total, calls, costUSD },
//     byFeature: { coachChat: {in, out, total, calls, costUSD}, ... },
//     byDay:     { "2026-05-24": {in, out, total, calls, costUSD}, ... },
//   }
//
// HayahSettings → Token Usage reads this and exposes a reset button.
// Pruning keeps the byDay map bounded so localStorage doesn't grow.
//
// costUSD is computed at write time via costForFeature() so the cost
// reflects the model that actually serviced the call (per the primary
// MODELS[feature] assignment — fallback degrades are not modelled, see
// aiConfig MODEL_PRICING_USD_PER_1M for the caveat).

import { costForFeature } from "./aiConfig";

const KEY = "hayahTokenUsage";

function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function emptyState() {
  return {
    sinceTs: Date.now(),
    totals: { in: 0, out: 0, total: 0, calls: 0, costUSD: 0 },
    byFeature: {},
    byDay: {},
  };
}

function loadState() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw || typeof raw !== "object" || !raw.totals) return emptyState();
    return {
      sinceTs: raw.sinceTs || Date.now(),
      totals: { in: 0, out: 0, total: 0, calls: 0, costUSD: 0, ...raw.totals },
      byFeature: raw.byFeature || {},
      byDay: raw.byDay || {},
    };
  } catch { return emptyState(); }
}

function saveState(s) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {}
}

// Called by geminiAI after every Gemini response (success or
// recoverable partial). `usage` is the raw response.usageMetadata
// from the v1beta API: { promptTokenCount, candidatesTokenCount,
// totalTokenCount, cachedContentTokenCount?, thoughtsTokenCount? }.
// `servedModel` is the model id that actually answered (may differ from
// the feature's primary if FALLBACK_CHAINS degraded the call).
//
// Output tokens are visible candidates + reasoning/thinking tokens —
// Google bills 2.5 Pro/Flash thinking at the OUTPUT rate, and they can
// be 5–20x the visible output on Pro. Dropping them was the biggest
// undercount source pre-fix.
export function recordUsage(feature, usage, servedModel = null) {
  if (!usage || typeof usage !== "object") return;
  const inTok     = Number(usage.promptTokenCount) || 0;
  const cachedTok = Number(usage.cachedContentTokenCount) || 0;
  const visibleOut = Number(usage.candidatesTokenCount) || 0;
  const thoughtTok = Number(usage.thoughtsTokenCount) || 0;
  const outTok    = visibleOut + thoughtTok;
  const total     = Number(usage.totalTokenCount) || (inTok + outTok);
  if (total <= 0) return;
  const cost   = costForFeature(feature, inTok, outTok, cachedTok, servedModel);

  const s = loadState();
  s.totals.in      += inTok;
  s.totals.out     += outTok;
  s.totals.total   += total;
  s.totals.calls   += 1;
  s.totals.costUSD += cost;

  const f = s.byFeature[feature] || { in: 0, out: 0, total: 0, calls: 0, costUSD: 0 };
  f.in      += inTok;
  f.out     += outTok;
  f.total   += total;
  f.calls   += 1;
  f.costUSD += cost;
  // Stamp the most recent call so UI surfaces can render "last generation
  // cost: $X" badges without duplicating the cost math.
  f.lastCall = {
    ts: Date.now(),
    costUSD: cost,
    inTok,
    outTok,
    cachedTok,
    model: servedModel || null,
  };
  s.byFeature[feature] = f;

  const dKey = localDateStr();
  const dr = s.byDay[dKey] || { in: 0, out: 0, total: 0, calls: 0, costUSD: 0 };
  dr.in      += inTok;
  dr.out     += outTok;
  dr.total   += total;
  dr.calls   += 1;
  dr.costUSD += cost;
  s.byDay[dKey] = dr;

  // Prune byDay to last 30 days to keep localStorage bounded.
  const cutoff = Date.now() - 30 * 86400000;
  for (const key of Object.keys(s.byDay)) {
    const t = Date.parse(`${key}T00:00:00`);
    if (Number.isFinite(t) && t < cutoff) delete s.byDay[key];
  }
  saveState(s);
}

export function getUsageSummary() {
  return loadState();
}

// Returns the most recent call's usage + cost for a feature, or null if
// nothing has been recorded yet. Shape: { ts, costUSD, inTok, outTok,
// cachedTok, model }. Used by per-surface cost badges so the user can
// see what each generation actually cost.
export function getLastCall(feature) {
  const s = loadState();
  return s.byFeature[feature]?.lastCall || null;
}

export function resetUsage() {
  saveState(emptyState());
  try { window.dispatchEvent(new StorageEvent("storage", { key: KEY })); } catch {}
}

// One-time baseline seed. Records the user's actual Google Cloud
// spend prior to v2 launch (when the dashboard didn't exist) so the
// running total reflects real lifetime spend, not just post-v2 calls.
// Idempotent via a localStorage flag — survives Reset Counter so the
// baseline isn't accidentally re-added.
const BASELINE_FLAG       = "hayahTokenBaselineSeeded_v2";
const BASELINE_SINCE_DATE = "2026-04-27";  // $10 credit loaded
const BASELINE_COST_USD   = 3.13;          // actual Google Cloud spend as of 2026-05-26

export function seedBaselineIfNeeded() {
  try {
    if (localStorage.getItem(BASELINE_FLAG)) return false;
    const s = loadState();
    s.sinceTs = Date.parse(`${BASELINE_SINCE_DATE}T00:00:00`);
    // v2 baseline: REPLACE running total instead of adding. The previous
    // logged costs underreported reality (missing thinking/cached/served-model
    // pricing — see git log for fixes). Stamping the actual Google Cloud
    // spend gives users an accurate lifetime starting point; new calls
    // accumulate on top with the corrected pricing.
    s.totals.costUSD = BASELINE_COST_USD;
    saveState(s);
    localStorage.setItem(BASELINE_FLAG, String(Date.now()));
    return true;
  } catch { return false; }
}

// Last-7-days array, oldest first, with zero-fill for days that had
// no calls. Convenient for the Settings dashboard.
export function getLast7Days() {
  const s = loadState();
  const out = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = localDateStr(d);
    out.push({ date: key, ...(s.byDay[key] || { in: 0, out: 0, total: 0, calls: 0, costUSD: 0 }) });
  }
  return out;
}
